import React, { useState, useEffect, useRef } from 'react';
import { Bell, RefreshCw, Send, Palette, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { dashboardAPI, reportsAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function Header({ onRefresh, academicYear, onYearChange, years, sidebarWidth = 260 }) {
  const { user, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const MGMT_ROLES = new Set(['admin','super_admin','vice_principal','principal','management','coordinator','vice principal']);
  const isTeacherView = user?.role && !MGMT_ROLES.has((user.role || '').toLowerCase().replace(/[\s-]+/g, '_'));
  const [notifOpen,  setNotifOpen]  = useState(false);
  const [themeOpen,  setThemeOpen]  = useState(false);
  const [missing,    setMissing]    = useState(null);
  const [sending,    setSending]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const themeRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    reportsAPI.getMissingData({ academicYearId: academicYear })
      .then(r => setMissing(r.data))
      .catch(() => {});
  }, [academicYear]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    onRefresh?.();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const sendTelegram = async () => {
    setSending(true);
    try {
      await reportsAPI.sendTelegram({ type: 'daily' });
      toast.success('Report sent to Telegram!');
    } catch { toast.error('Telegram send failed. Check bot settings.'); }
    finally { setSending(false); }
  };

  const badgeCount = (missing?.missingAttendanceToday?.length || 0) + (missing?.pendingGrievancesCount > 0 ? 1 : 0);
  const current = THEMES[theme];

  return (
    <header
      className="fixed top-0 right-0 z-20 h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-6 shadow-sm transition-all duration-300 ease-in-out"
      style={{ left: sidebarWidth }}
    >
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-display font-semibold text-slate-800">
          {isTeacherView ? 'Teachers Dashboard' : 'Kulish Suite'}
        </h1>
        <span className="hidden sm:block text-slate-400 text-sm">
          {isTeacherView ? '— Your personal performance overview' : '— The Kulish School'}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {/* Academic Year Selector */}
        <select
          value={academicYear}
          onChange={e => onYearChange?.(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
        >
          {(years || []).map(y => (
            <option key={y.id} value={y.id}>{y.year_label}</option>
          ))}
        </select>

        {/* Refresh */}
        <button
          onClick={handleRefresh}
          className="p-2 rounded-lg hover:bg-primary-50 text-slate-500 hover:text-primary-600 transition-colors"
          title="Refresh data"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
        </button>

        {/* Telegram */}
        {isAdmin && (
          <button
            onClick={sendTelegram}
            disabled={sending}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors disabled:opacity-60"
            title="Send report to Telegram"
          >
            <Send size={14} />
            <span>{sending ? 'Sending...' : 'Telegram'}</span>
          </button>
        )}

        {/* ── Theme Picker Dropdown ──────────────────────────────── */}
        <div className="relative" ref={themeRef}>
          <button
            onClick={() => { setThemeOpen(o => !o); setNotifOpen(false); }}
            title="Change theme"
            className={`
              flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all duration-200
              ${themeOpen
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-600'
              }
            `}
          >
            {/* Live swatch showing current theme colours */}
            <span
              className="w-4 h-4 rounded-full flex-shrink-0 ring-1 ring-black/10"
              style={{ background: `linear-gradient(135deg, ${current.primary} 0%, ${current.accent} 100%)` }}
            />
            <Palette size={15} />
          </button>

          {themeOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 animate-fade-in">
              {/* Panel header */}
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/70">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Appearance</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">Choose a theme</p>
              </div>

              {/* Theme options */}
              <div className="p-2 space-y-1">
                {Object.values(THEMES).map(t => (
                  <button
                    key={t.key}
                    onClick={() => { setTheme(t.key); setThemeOpen(false); }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150
                      ${theme === t.key
                        ? 'bg-primary-50 border border-primary-200'
                        : 'hover:bg-slate-50 border border-transparent'
                      }
                    `}
                  >
                    {/* Gradient colour swatch */}
                    <span
                      className="w-9 h-9 rounded-lg flex-shrink-0 shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${t.primary} 0%, ${t.accent} 100%)` }}
                    />
                    {/* Name + tagline */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold leading-tight ${theme === t.key ? 'text-primary-800' : 'text-slate-700'}`}>
                        {t.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{t.label}</p>
                    </div>
                    {/* Active checkmark badge */}
                    {theme === t.key && (
                      <span className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                        <Check size={11} className="text-white" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/70">
                <p className="text-[11px] text-slate-400 text-center">Theme saved automatically</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Notifications ─────────────────────────────────────── */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(o => !o); setThemeOpen(false); }}
            className="relative p-2 rounded-lg hover:bg-primary-50 text-slate-500 hover:text-primary-600 transition-colors"
          >
            <Bell size={20} />
            {badgeCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 animate-fade-in">
              <h3 className="font-semibold text-slate-800 mb-3">Notifications</h3>
              {missing?.missingAttendanceToday?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-amber-600 uppercase mb-1">Missing Attendance Today</p>
                  <div className="space-y-1">
                    {missing.missingAttendanceToday.slice(0, 4).map(e => (
                      <p key={e.employee_code} className="text-sm text-slate-600">• {e.name} ({e.employee_code})</p>
                    ))}
                    {missing.missingAttendanceToday.length > 4 && (
                      <p className="text-xs text-slate-400">+{missing.missingAttendanceToday.length - 4} more</p>
                    )}
                  </div>
                </div>
              )}
              {missing?.pendingGrievancesCount > 0 && (
                <p className="text-sm text-slate-600">⚠️ {missing.pendingGrievancesCount} pending grievances need attention</p>
              )}
              {!badgeCount && <p className="text-sm text-slate-400">No new notifications</p>}
            </div>
          )}
        </div>

        {/* ── User avatar ───────────────────────────────────────── */}
        <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
          <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-semibold">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-slate-700 leading-none">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
